import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * One menu item that a page wants to publish into the StartMenu's "Page
 * context" section. Must be a `<MenuItem>` from `react-aria-components` with
 * both a React `key` and an `id` set — react-aria's collection model
 * identifies items by `id`. Owning state and modals belong with the host
 * that registers the items; only the trigger element crosses the context.
 */
export type StartMenuPageContextItem = ReactElement;

interface ContextValue {
  itemsBySource: Map<string, StartMenuPageContextItem[]>;
  setItemsForSource: (source: string, items: StartMenuPageContextItem[] | null) => void;
}

const StartMenuPageContext = createContext<ContextValue>({
  itemsBySource: new Map(),
  setItemsForSource: () => {},
});

export function StartMenuPageContextProvider({ children }: { children: ReactNode }) {
  const [itemsBySource, setItemsBySource] = useState<Map<string, StartMenuPageContextItem[]>>(new Map());

  const setItemsForSource = useCallback((source: string, items: StartMenuPageContextItem[] | null) => {
    setItemsBySource(previous => {
      const next = new Map(previous);
      if (items === null || items.length === 0) {
        next.delete(source);
      } else {
        next.set(source, items);
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ itemsBySource, setItemsForSource }), [itemsBySource, setItemsForSource]);
  return <StartMenuPageContext.Provider value={value}>{children}</StartMenuPageContext.Provider>;
}

/**
 * Flattened items across all registered sources, ordered by registration order
 * within each source. Sources are sorted by their string key so the menu order
 * is stable when multiple hosts contribute.
 */
export function useStartMenuPageContextItems(): StartMenuPageContextItem[] {
  const { itemsBySource } = useContext(StartMenuPageContext);
  return useMemo(() => {
    const sources = [...itemsBySource.keys()].sort();
    return sources.flatMap(source => itemsBySource.get(source) ?? []);
  }, [itemsBySource]);
}

/**
 * Publishes `items` under a unique `source` key into the StartMenu's "Page
 * context" section while the caller is mounted. Multiple hosts can register
 * simultaneously by using distinct source keys. Pass a memoised array — the
 * effect resets that source's slice on every reference change, then clears
 * it on unmount.
 */
export function useRegisterStartMenuPageContextItems(source: string, items: StartMenuPageContextItem[]) {
  const { setItemsForSource } = useContext(StartMenuPageContext);
  useEffect(() => {
    setItemsForSource(source, items);
    return () => {
      setItemsForSource(source, null);
    };
  }, [source, items, setItemsForSource]);
}
